// ============================================
// Therapist Home
// ============================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  getDocs,
  getDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Event,
  EventTherapist,
  BookingRequest,
  Session,
  DutyStatus,
  STATUS_COLORS,
  TherapistAttendance,
  BreakSegment,
} from '../../types';

// Get today's date in YYYY-MM-DD format
const getTodayDateId = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

export default function TherapistHome() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [availableEvents, setAvailableEvents] = React.useState<Event[]>([]);
  const [currentEvent, setCurrentEvent] = React.useState<Event | null>(null);
  const [membership, setMembership] = React.useState<EventTherapist | null>(null);
  const [pendingRequests, setPendingRequests] = React.useState<BookingRequest[]>([]);
  const [myAcceptedRequest, setMyAcceptedRequest] = React.useState<BookingRequest | null>(null);
  const [activeSession, setActiveSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [todayAttendance, setTodayAttendance] = React.useState<TherapistAttendance | null>(null);
  const [otherTherapists, setOtherTherapists] = React.useState<EventTherapist[]>([]);

  // Heartbeat interval
  React.useEffect(() => {
    if (!currentEvent || !user) return;

    const sendHeartbeat = async () => {
      try {
        await updateDoc(
          doc(db, 'events', currentEvent.id, 'therapists', user.uid),
          { lastHeartbeatAt: serverTimestamp() }
        );
      } catch (err) {
        console.error('Heartbeat error:', err);
      }
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [currentEvent, user]);

  // Find all live events where user is a therapist
  React.useEffect(() => {
    if (!user) return;

    const findMemberships = async () => {
      try {
        // Query only live events (therapists can read live events)
        const liveEventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
        const eventsSnap = await getDocs(liveEventsQuery);

        const myEvents: Event[] = [];

        for (const eventDoc of eventsSnap.docs) {
          const event = { id: eventDoc.id, ...eventDoc.data() } as Event;

          try {
            // Check if user is a therapist in this event
            const therapistDoc = await getDocs(
              query(
                collection(db, 'events', event.id, 'therapists'),
                where('__name__', '==', user.uid)
              )
            );

            if (!therapistDoc.empty) {
              myEvents.push(event);
            }
          } catch (err) {
            console.log('No access to therapist data in event:', event.id);
          }
        }

        setAvailableEvents(myEvents);

        // Auto-select if only one event
        if (myEvents.length === 1) {
          setCurrentEvent(myEvents[0]);
        }
      } catch (err) {
        console.error('Error finding memberships:', err);
      }
      setLoading(false);
    };

    findMemberships();
  }, [user]);

  // Handle event selection
  const selectEvent = (event: Event) => {
    setCurrentEvent(event);
  };

  // Switch to different event
  const switchEvent = () => {
    setCurrentEvent(null);
    setMembership(null);
    setTodayAttendance(null);
    setPendingRequests([]);
    setMyAcceptedRequest(null);
  };

  // Subscribe to membership, requests, and session
  React.useEffect(() => {
    if (!currentEvent || !user) return;

    // Membership
    const membershipUnsub = onSnapshot(
      doc(db, 'events', currentEvent.id, 'therapists', user.uid),
      (docSnap) => {
        if (docSnap.exists()) {
          setMembership({ uid: docSnap.id, ...docSnap.data() } as EventTherapist);
        }
      }
    );

    // Pending requests (for open_queue mode)
    const requestsQuery = query(
      collection(db, 'events', currentEvent.id, 'bookingRequests'),
      where('status', 'in', ['pending', 'assigned'])
    );
    const requestsUnsub = onSnapshot(requestsQuery, (snapshot) => {
      const requests = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() } as BookingRequest))
        .filter((r) => {
          // Show if pending in open_queue or assigned to me
          if (r.status === 'pending' && currentEvent.assignmentMode === 'open_queue') return true;
          if (r.assignedTherapistId === user.uid) return true;
          return false;
        });
      setPendingRequests(requests);
    });

    // My accepted request
    const myRequestQuery = query(
      collection(db, 'events', currentEvent.id, 'bookingRequests'),
      where('assignedTherapistId', '==', user.uid),
      where('status', '==', 'accepted')
    );
    const myRequestUnsub = onSnapshot(myRequestQuery, (snapshot) => {
      if (!snapshot.empty) {
        setMyAcceptedRequest({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as BookingRequest);
      } else {
        setMyAcceptedRequest(null);
      }
    });

    // Active session
    const sessionQuery = query(
      collection(db, 'events', currentEvent.id, 'sessions'),
      where('therapistId', '==', user.uid),
      where('status', '==', 'active')
    );
    const sessionUnsub = onSnapshot(sessionQuery, (snapshot) => {
      if (!snapshot.empty) {
        setActiveSession({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Session);
      } else {
        setActiveSession(null);
      }
    });

    // Today's attendance
    const todayId = getTodayDateId();
    const attendanceUnsub = onSnapshot(
      doc(db, 'events', currentEvent.id, 'therapists', user.uid, 'attendance', todayId),
      (docSnap) => {
        if (docSnap.exists()) {
          setTodayAttendance({ ...docSnap.data() } as TherapistAttendance);
        } else {
          setTodayAttendance(null);
        }
      }
    );

    // All therapists (to show team status)
    const allTherapistsUnsub = onSnapshot(
      collection(db, 'events', currentEvent.id, 'therapists'),
      (snapshot) => {
        const others = snapshot.docs
          .map((doc) => ({ uid: doc.id, ...doc.data() } as EventTherapist))
          .filter((t) => t.uid !== user.uid) // Exclude self
          .sort((a, b) => {
            // Sort by status: on_duty first, then break, then off_duty
            const statusOrder = { on_duty: 0, break: 1, off_duty: 2 };
            const aOrder = statusOrder[a.dutyStatus] ?? 2;
            const bOrder = statusOrder[b.dutyStatus] ?? 2;
            if (aOrder !== bOrder) return aOrder - bOrder;
            // Then by name
            return (a.publicDisplayName || a.displayName).localeCompare(b.publicDisplayName || b.displayName);
          });
        setOtherTherapists(others);
      }
    );

    return () => {
      membershipUnsub();
      requestsUnsub();
      myRequestUnsub();
      sessionUnsub();
      attendanceUnsub();
      allTherapistsUnsub();
    };
  }, [currentEvent, user]);

  // Check in for today (creates or updates attendance record)
  const checkInToday = async () => {
    if (!currentEvent || !user) return;

    const todayId = getTodayDateId();
    const attendanceRef = doc(db, 'events', currentEvent.id, 'therapists', user.uid, 'attendance', todayId);

    // Check if already checked in today
    const existingDoc = await getDoc(attendanceRef);

    if (existingDoc.exists()) {
      const data = existingDoc.data() as TherapistAttendance;
      // If they checked out, add a new segment
      if (data.checkedOutAt) {
        const newSegment = {
          checkedInAt: Timestamp.now(),
        };
        const segments = data.segments || [];
        // Move previous check-in/out to segments if not already there
        if (segments.length === 0 || segments[segments.length - 1].checkedOutAt) {
          segments.push({
            checkedInAt: data.checkedInAt,
            checkedOutAt: data.checkedOutAt,
            minutes: data.venueMinutes,
          });
        }
        segments.push(newSegment);

        await setDoc(attendanceRef, {
          ...data,
          checkedInAt: Timestamp.now(),
          checkedOutAt: null,
          venueMinutes: null,
          segments,
        }, { merge: true });
      }
      // Already checked in, nothing to do
    } else {
      // Create new attendance record
      await setDoc(attendanceRef, {
        date: todayId,
        therapistId: user.uid,
        checkedInAt: serverTimestamp(),
        checkedOutAt: null,
        segments: [],
      });
    }
  };

  // Check out for today
  const checkOutToday = async () => {
    if (!currentEvent || !user || !todayAttendance) return;

    const todayId = getTodayDateId();
    const attendanceRef = doc(db, 'events', currentEvent.id, 'therapists', user.uid, 'attendance', todayId);

    const checkedInAt = todayAttendance.checkedInAt?.toDate?.() || new Date(todayAttendance.checkedInAt as any);
    const now = new Date();
    const currentSegmentMinutes = Math.round((now.getTime() - checkedInAt.getTime()) / 60000);

    // Calculate total venue minutes including previous segments
    let totalMinutes = currentSegmentMinutes;
    if (todayAttendance.segments) {
      todayAttendance.segments.forEach((seg) => {
        if (seg.minutes) {
          totalMinutes += seg.minutes;
        }
      });
    }

    await setDoc(attendanceRef, {
      checkedOutAt: serverTimestamp(),
      venueMinutes: totalMinutes,
    }, { merge: true });
  };

  const setDutyStatus = async (status: DutyStatus) => {
    if (!currentEvent || !user) return;

    // Check for active session in Firestore before allowing status change
    try {
      const checkActiveSession = httpsCallable(functions, 'checkTherapistActiveSession');
      const result = await checkActiveSession({ eventId: currentEvent.id }) as any;

      if (result.data.hasActiveSession) {
        const session = result.data;
        alert(`You have an active session at Table ${session.tableNo}, Seat ${session.seatNo}. Please complete or report the session before changing your status.`);
        // Navigate to the session
        navigate(`/t/session/${session.sessionId}`);
        return;
      }
    } catch (err) {
      console.error('Error checking active session:', err);
      // Continue if check fails - the UI will handle it
    }

    const updates: Record<string, any> = { dutyStatus: status };

    // Check in for today when going on duty
    if (status === 'on_duty') {
      // Check in to daily attendance if not already checked in today
      if (!todayAttendance || todayAttendance.checkedOutAt) {
        await checkInToday();
      }
      // Also update legacy checkedInAt if not set
      if (!membership?.checkedInAt) {
        updates.checkedInAt = serverTimestamp();
        updates.checkedOutAt = null;
      }

      // End current break if coming back from break
      if (membership?.dutyStatus === 'break' && membership?.breakStartedAt) {
        updates.breakStartedAt = null;
        // End the break in attendance record
        await endBreakInAttendance();
      }
    }

    // Start break tracking
    if (status === 'break') {
      updates.breakStartedAt = serverTimestamp();
      // Add break start to attendance record
      await startBreakInAttendance();
    }

    // Update UI status
    if (status === 'off_duty') {
      updates.uiStatus = 'grey';
    } else if (status === 'break') {
      updates.uiStatus = 'blue';
    } else if (status === 'on_duty' && !activeSession) {
      updates.uiStatus = 'green';
    }

    await updateDoc(doc(db, 'events', currentEvent.id, 'therapists', user.uid), updates);
  };

  // Start a break segment in today's attendance
  const startBreakInAttendance = async () => {
    if (!currentEvent || !user) return;

    const todayId = getTodayDateId();
    const attendanceRef = doc(db, 'events', currentEvent.id, 'therapists', user.uid, 'attendance', todayId);

    const existingDoc = await getDoc(attendanceRef);
    if (!existingDoc.exists()) return;

    const data = existingDoc.data() as TherapistAttendance;
    const breakSegments: BreakSegment[] = data.breakSegments || [];

    // Add new break segment
    breakSegments.push({
      startedAt: Timestamp.now(),
    });

    await setDoc(attendanceRef, { breakSegments }, { merge: true });
  };

  // End the current break segment in today's attendance
  const endBreakInAttendance = async () => {
    if (!currentEvent || !user) return;

    const todayId = getTodayDateId();
    const attendanceRef = doc(db, 'events', currentEvent.id, 'therapists', user.uid, 'attendance', todayId);

    const existingDoc = await getDoc(attendanceRef);
    if (!existingDoc.exists()) return;

    const data = existingDoc.data() as TherapistAttendance;
    const breakSegments: BreakSegment[] = data.breakSegments || [];

    // Find the last break segment without an end time
    const lastBreakIndex = breakSegments.findIndex((seg) => !seg.endedAt);
    if (lastBreakIndex === -1) return;

    const lastBreak = breakSegments[lastBreakIndex];
    const startedAt = lastBreak.startedAt?.toDate?.() || new Date(lastBreak.startedAt as any);
    const now = new Date();
    const breakMinutes = Math.round((now.getTime() - startedAt.getTime()) / 60000);

    breakSegments[lastBreakIndex] = {
      ...lastBreak,
      endedAt: Timestamp.now(),
      minutes: breakMinutes,
    };

    // Calculate total break minutes
    let totalBreakMinutes = 0;
    breakSegments.forEach((seg) => {
      if (seg.minutes) {
        totalBreakMinutes += seg.minutes;
      }
    });

    await setDoc(attendanceRef, { breakSegments, totalBreakMinutes }, { merge: true });
  };

  const checkOut = async () => {
    if (!currentEvent || !user) return;

    // End any active break first
    if (membership?.dutyStatus === 'break' && membership?.breakStartedAt) {
      await endBreakInAttendance();
    }

    // Check out from daily attendance
    if (todayAttendance && !todayAttendance.checkedOutAt) {
      await checkOutToday();
    }

    // Also update legacy checkedOutAt
    await updateDoc(doc(db, 'events', currentEvent.id, 'therapists', user.uid), {
      dutyStatus: 'off_duty',
      uiStatus: 'grey',
      checkedOutAt: serverTimestamp(),
      breakStartedAt: null,
    });
  };

  const acceptRequest = async (requestId: string, etaMinutes: number) => {
    try {
      const acceptBooking = httpsCallable(functions, 'acceptBooking');
      await acceptBooking({ eventId: currentEvent?.id, requestId, etaMinutes });
    } catch (err: any) {
      alert(err.message || 'Failed to accept request');
    }
  };

  const startSession = async () => {
    if (!myAcceptedRequest || !currentEvent) return;
    try {
      const startSessionFn = httpsCallable(functions, 'startSession');
      await startSessionFn({ eventId: currentEvent.id, requestId: myAcceptedRequest.id });
    } catch (err: any) {
      alert(err.message || 'Failed to start session');
    }
  };

  // Walk-in booking state
  const [showWalkinForm, setShowWalkinForm] = React.useState(false);
  const [walkinTable, setWalkinTable] = React.useState<number | null>(null);
  const [walkinSeat, setWalkinSeat] = React.useState<number | null>(null);
  const [walkinMinutes, setWalkinMinutes] = React.useState<number | null>(null);
  const [walkinSubmitting, setWalkinSubmitting] = React.useState(false);

  const createWalkinBooking = async () => {
    if (!currentEvent || !walkinTable || !walkinSeat || !walkinMinutes) return;

    setWalkinSubmitting(true);
    try {
      const createWalkinBookingFn = httpsCallable(functions, 'createWalkinBooking');
      await createWalkinBookingFn({
        eventId: currentEvent.id,
        tableNo: walkinTable,
        seatNo: walkinSeat,
        minutes: walkinMinutes,
      });
      // Reset form
      setShowWalkinForm(false);
      setWalkinTable(null);
      setWalkinSeat(null);
      setWalkinMinutes(null);
    } catch (err: any) {
      alert(err.message || 'Failed to create walk-in booking');
    } finally {
      setWalkinSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="therapist-home loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show event selection if multiple events available
  if (!currentEvent && availableEvents.length > 1) {
    return (
      <div className="therapist-home">
        <div style={{ padding: '1rem' }}>
          <h2 style={{ marginBottom: '0.5rem' }}>Select Event</h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            You're part of multiple live events. Choose which one to work at today.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {availableEvents.map((event) => (
              <button
                key={event.id}
                onClick={() => selectEvent(event)}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '2px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {event.name}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  {event.venueName}
                </div>
                {event.startDate && event.endDate && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                    {new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="therapist-home no-event">
        <div className="empty-state">
          <h2>No Active Event</h2>
          <p>You're not part of any live event.</p>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Go to Profile to redeem an invite code.
          </p>
          <button onClick={() => navigate('/t/profile')} className="btn btn-primary btn-large">
            Go to Profile
          </button>
        </div>
      </div>
    );
  }

  // If we have an active session, redirect to session page
  if (activeSession) {
    navigate(`/t/session/${activeSession.id}`);
    return null;
  }

  return (
    <div className="therapist-home">
      {/* Event Info */}
      <div className="event-info" style={{ position: 'relative' }}>
        <h2>{currentEvent.name}</h2>
        <p>{currentEvent.venueName}</p>
        {availableEvents.length > 1 && (
          <button
            onClick={switchEvent}
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              padding: '0.5rem 0.75rem',
              fontSize: '0.75rem',
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            Switch Event
          </button>
        )}
      </div>

      {/* Status Display */}
      <div className="status-controls">
        <div style={{
          textAlign: 'center',
          marginBottom: '1rem',
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Your Status
          </div>
          <div
            style={{
              display: 'inline-block',
              padding: '0.75rem 1.5rem',
              borderRadius: '12px',
              backgroundColor: STATUS_COLORS[membership?.uiStatus || 'grey'],
              color: '#fff',
              fontSize: '1.25rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: `0 4px 12px ${STATUS_COLORS[membership?.uiStatus || 'grey']}40`,
            }}
          >
            {membership?.dutyStatus === 'on_duty' ? 'ON DUTY' :
             membership?.dutyStatus === 'break' ? 'ON BREAK' :
             membership?.dutyStatus === 'off_duty' ? 'OFF DUTY' : 'OFF DUTY'}
          </div>
        </div>

        <div className="status-buttons" style={{
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => setDutyStatus('on_duty')}
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '10px',
              border: membership?.dutyStatus === 'on_duty' ? 'none' : '2px solid var(--color-border)',
              backgroundColor: membership?.dutyStatus === 'on_duty' ? STATUS_COLORS.green : 'var(--color-bg-secondary)',
              color: membership?.dutyStatus === 'on_duty' ? '#fff' : 'var(--color-text)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px',
            }}
          >
            On Duty
          </button>
          <button
            onClick={() => setDutyStatus('break')}
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '10px',
              border: membership?.dutyStatus === 'break' ? 'none' : '2px solid var(--color-border)',
              backgroundColor: membership?.dutyStatus === 'break' ? STATUS_COLORS.blue : 'var(--color-bg-secondary)',
              color: membership?.dutyStatus === 'break' ? '#fff' : 'var(--color-text)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px',
            }}
          >
            Break
          </button>
          {/* Check Out button - show when on duty/break, or has attendance record without checkout */}
          {(membership?.dutyStatus === 'on_duty' || membership?.dutyStatus === 'break' || (todayAttendance && !todayAttendance.checkedOutAt)) && (
            <button
              onClick={checkOut}
              style={{
                padding: '0.875rem 1.5rem',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#f59e0b',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '100px',
              }}
            >
              Check Out
            </button>
          )}
        </div>

        {/* Today's Attendance Info */}
        {todayAttendance && !todayAttendance.checkedOutAt && (
          <div style={{
            marginTop: '0.75rem',
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            textAlign: 'center',
          }}>
            Checked in at {todayAttendance.checkedInAt?.toDate?.()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '-'}
          </div>
        )}

        {todayAttendance?.checkedOutAt && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: 'var(--color-bg-secondary)',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
              Checked out • {todayAttendance.venueMinutes ? `${Math.floor(todayAttendance.venueMinutes / 60)}h ${todayAttendance.venueMinutes % 60}m at venue today` : 'Venue time calculated'}
            </div>
            <button
              className="btn btn-primary btn-large"
              onClick={() => setDutyStatus('on_duty')}
              style={{ width: '100%' }}
            >
              Check In Again
            </button>
          </div>
        )}
      </div>

      {/* Team Status */}
      {otherTherapists.length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '12px',
          marginBottom: '1rem',
        }}>
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem',
          }}>
            Team ({otherTherapists.filter(t => t.dutyStatus !== 'off_duty').length} on duty)
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}>
            {otherTherapists.map((therapist) => (
              <div
                key={therapist.uid}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
                title={`${therapist.publicDisplayName || therapist.displayName} - ${
                  therapist.dutyStatus === 'on_duty' ? (therapist.activeSessionId ? 'In Session' : 'Available') :
                  therapist.dutyStatus === 'break' ? 'On Break' : 'Off Duty'
                }`}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: `3px solid ${STATUS_COLORS[therapist.uiStatus || 'grey']}`,
                  backgroundColor: 'var(--color-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  opacity: therapist.dutyStatus === 'off_duty' ? 0.5 : 1,
                }}>
                  {therapist.photoUrl && therapist.photoStatus === 'approved' ? (
                    <img
                      src={therapist.thumbnailUrl || therapist.photoUrl}
                      alt={therapist.publicDisplayName || therapist.displayName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: therapist.dutyStatus === 'off_duty' ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)',
                    }}>
                      {(therapist.publicDisplayName || therapist.displayName || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <span style={{
                  fontSize: '0.625rem',
                  color: therapist.dutyStatus === 'off_duty' ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)',
                  maxWidth: '48px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}>
                  {(therapist.publicDisplayName || therapist.displayName || '').split(' ')[0]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Accepted Request */}
      {myAcceptedRequest && (
        <div className="accepted-request">
          <h3>Accepted Request</h3>
          <div className="request-card large">
            <div className="location">
              Table {myAcceptedRequest.tableNo}, Seat {myAcceptedRequest.seatNo}
            </div>
            <div className="minutes">{myAcceptedRequest.requestedMinutes} minutes</div>
            <div className="eta">ETA: {myAcceptedRequest.etaMinutes} min</div>
            <button className="btn btn-success btn-xlarge" onClick={startSession}>
              START SESSION
            </button>
          </div>
        </div>
      )}

      {/* Pending Requests */}
      {!myAcceptedRequest && membership?.dutyStatus === 'on_duty' && (
        <div className="pending-requests">
          <h3>Available Requests</h3>
          {pendingRequests.length === 0 ? (
            <div className="empty">No pending requests</div>
          ) : (
            <div className="requests-list">
              {pendingRequests.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onAccept={acceptRequest}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Walk-in Booking */}
      {!myAcceptedRequest && membership?.dutyStatus === 'on_duty' && (
        <div className="walkin-booking" style={{ marginTop: '2rem' }}>
          {!showWalkinForm ? (
            <button
              className="btn btn-secondary btn-large"
              onClick={() => setShowWalkinForm(true)}
              style={{ width: '100%' }}
            >
              + Add Walk-in Booking
            </button>
          ) : (
            <div style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderRadius: '8px',
              padding: '1rem',
            }}>
              <h3 style={{ marginTop: 0 }}>Walk-in Booking</h3>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                For players who approached you directly
              </p>

              {/* Table Selection */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Table</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {Array.from({ length: currentEvent.tablesCount }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      className={`btn ${walkinTable === num ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setWalkinTable(num)}
                      style={{ minWidth: '48px' }}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seat Selection */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Seat</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {Array.from({ length: currentEvent.seatsPerTable }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      className={`btn ${walkinSeat === num ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setWalkinSeat(num)}
                      style={{ minWidth: '48px' }}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration Selection */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Duration</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {currentEvent.bookingMinuteOptions.map((mins) => (
                    <button
                      key={mins}
                      className={`btn ${walkinMinutes === mins ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setWalkinMinutes(mins)}
                      style={{ minWidth: '64px' }}
                    >
                      {mins} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              {walkinTable && walkinSeat && walkinMinutes && (
                <div style={{
                  backgroundColor: 'var(--color-bg)',
                  borderRadius: '4px',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  textAlign: 'center',
                }}>
                  Table {walkinTable}, Seat {walkinSeat} - {walkinMinutes} min
                  <br />
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    {currentEvent.currency} {(walkinMinutes * currentEvent.pricePerMinute).toFixed(0)}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowWalkinForm(false);
                    setWalkinTable(null);
                    setWalkinSeat(null);
                    setWalkinMinutes(null);
                  }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={createWalkinBooking}
                  disabled={!walkinTable || !walkinSeat || !walkinMinutes || walkinSubmitting}
                  style={{ flex: 2 }}
                >
                  {walkinSubmitting ? 'Creating...' : 'Start Session'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Request Card Component
interface RequestCardProps {
  request: BookingRequest;
  onAccept: (requestId: string, etaMinutes: number) => void;
}

function RequestCard({ request, onAccept }: RequestCardProps) {
  const [showEta, setShowEta] = React.useState(false);
  const [etaMinutes, setEtaMinutes] = React.useState(5); // Default 5 minutes

  // Format ETA display (show hours if >= 60 minutes)
  const formatEta = (minutes: number) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes} min`;
  };

  // Increment/decrement step based on current value
  const getStep = (current: number, direction: 'up' | 'down') => {
    if (direction === 'up') {
      if (current < 15) return 5;
      if (current < 60) return 15;
      return 30; // 30 min increments after 1 hour
    } else {
      if (current <= 15) return 5;
      if (current <= 60) return 15;
      return 30;
    }
  };

  const decreaseEta = () => {
    const step = getStep(etaMinutes, 'down');
    setEtaMinutes(Math.max(5, etaMinutes - step));
  };

  const increaseEta = () => {
    const step = getStep(etaMinutes, 'up');
    setEtaMinutes(Math.min(180, etaMinutes + step)); // Max 3 hours
  };

  // Quick select options
  const quickOptions = [5, 15, 30, 60, 90, 120];

  return (
    <div className="request-card">
      <div className="request-info">
        <div className="location">
          Table {request.tableNo}, Seat {request.seatNo}
        </div>
        <div className="minutes">{request.requestedMinutes} min</div>
        <div className="status">{request.status}</div>
      </div>

      {!showEta ? (
        <button className="btn btn-success btn-large" onClick={() => setShowEta(true)}>
          Accept
        </button>
      ) : (
        <div className="eta-selector" style={{ padding: '1rem' }}>
          <p style={{ marginBottom: '0.75rem', fontWeight: 600 }}>When can you start?</p>

          {/* ETA adjuster with +/- buttons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1rem',
          }}>
            <button
              onClick={decreaseEta}
              disabled={etaMinutes <= 5}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: etaMinutes <= 5 ? 'var(--color-bg)' : 'var(--color-bg-secondary)',
                color: etaMinutes <= 5 ? 'var(--color-text-secondary)' : 'var(--color-text)',
                fontSize: '1.5rem',
                fontWeight: 700,
                cursor: etaMinutes <= 5 ? 'not-allowed' : 'pointer',
              }}
            >
              −
            </button>
            <div style={{
              minWidth: '100px',
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--color-primary)',
            }}>
              {formatEta(etaMinutes)}
            </div>
            <button
              onClick={increaseEta}
              disabled={etaMinutes >= 180}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: etaMinutes >= 180 ? 'var(--color-bg)' : 'var(--color-bg-secondary)',
                color: etaMinutes >= 180 ? 'var(--color-text-secondary)' : 'var(--color-text)',
                fontSize: '1.5rem',
                fontWeight: 700,
                cursor: etaMinutes >= 180 ? 'not-allowed' : 'pointer',
              }}
            >
              +
            </button>
          </div>

          {/* Quick select chips */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            justifyContent: 'center',
            marginBottom: '1rem',
          }}>
            {quickOptions.map((mins) => (
              <button
                key={mins}
                onClick={() => setEtaMinutes(mins)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '20px',
                  border: etaMinutes === mins ? 'none' : '1px solid var(--color-border)',
                  backgroundColor: etaMinutes === mins ? 'var(--color-primary)' : 'var(--color-bg)',
                  color: etaMinutes === mins ? '#fff' : 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                {formatEta(mins)}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              className="btn btn-secondary btn-large"
              onClick={() => setShowEta(false)}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              className="btn btn-success btn-large"
              onClick={() => onAccept(request.id, etaMinutes)}
              style={{ flex: 2 }}
            >
              Accept ({formatEta(etaMinutes)})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
